// import type { Database } from '~/types';
// import {
//   type QueryFilter,
//   type CodeNamePair,
//   type RowWithCodeAndName,
//   type IdNamePair,
//   type RowWithIdAndName,
//   type ListResult,
//   getQueryPagination,
//   getResultsPagination,
// } from '~/types/database-query';
// import type { ValidTableName, ValidColumnName, Supabase } from '~/types/supabase-client';
import type { Database } from '~/types';
import {
  type QueryFilter,
  type CodeNamePair,
  type RowWithCodeAndName,
  type IdNamePair,
  type RowWithIdAndName,
  type ListResult,
  getQueryPagination,
  getResultsPagination,
} from '~/types/database-query.d';
import type { ValidTableName, ValidColumnName, Supabase } from '~/types/supabase-client';
import { cleanFilterValue } from '../helpers/query';

export type SortTableField<TableName extends ValidTableName> = {
  field: ValidColumnName<TableName>;
  ascending?: boolean;
  nullsFirst?: boolean;
};

export type FindOneTableQueryParams<TableName extends ValidTableName> = {
  sort?: SortTableField<TableName>;
  select?: string;
};

export type FindManyTableQueryParams<TableName extends ValidTableName> =
  FindOneTableQueryParams<TableName> & {
    page?: number;
    perPage?: number;
    filters?: QueryFilter[];
    search?: string;
  };

export type TableServiceOpts<TableName extends ValidTableName> = {
  defaultSort: SortTableField<TableName>;
  pk: ValidColumnName<TableName>;
  searchField?: ValidColumnName<TableName>;
};

export default abstract class TableService<
  TableName extends ValidTableName,
  TableSchema = Database['public']['Tables'][TableName]['Row'],
  UpdateSchema = Database['public']['Tables'][TableName]['Update'],
  InsertSchema = Database['public']['Tables'][TableName]['Insert']
> {
  constructor(
    protected supabase: Supabase,
    protected tableName: TableName,
    protected opts: TableServiceOpts<TableName>
  ) {}

  protected get table() {
    return this.supabase.from(this.tableName);
  }

  protected get pk(): ValidColumnName<TableName> {
    return this.opts.pk;
  }

  protected get sort() {
    return this.opts.defaultSort;
  }

  public async findOne(
    id: string,
    query?: FindOneTableQueryParams<TableName>
  ): Promise<TableSchema> {
    const result = await this.table
      .select(query?.select || '*')
      .eq(this.opts.pk, id)
      .limit(1)
      .single();
    if (result.error) throw { status: 500, message: result.error };
    if (!result.data) throw { status: 404, message: 'Not Found' };
    return result.data as TableSchema;
  }

  public async findAll<T = TableSchema>(
    query?: FindManyTableQueryParams<TableName>
  ): Promise<ListResult<T>> {
    const sort = query?.sort || this.sort;
    const pagination = getQueryPagination(query?.page || 1, query?.perPage || 50);
    try {
      const result = await (() => {
        const r = this.table
          .select(query?.select || '*', { count: 'estimated' })
          .range(pagination.startIndex, pagination.endIndex)
          .order(sort.field, { ascending: !!sort.ascending, nullsFirst: !!sort.nullsFirst });
        if (query?.filters) {
          query.filters.forEach((filter) => {
            r.filter(filter[0], filter[1], cleanFilterValue(filter[1], filter[2]));
          });
        }
        if (this.opts.searchField && query?.search) {
          r.textSearch(this.opts.searchField, query.search, {
            type: 'websearch',
            config: 'english',
          });
        }
        return r;
      })();
      if (result.error) throw { status: 500, message: result.error };
      if (result.count === null) throw { status: 500, message: 'Could not get result count.' };
      return {
        data: result.data as T[],
        pagination: getResultsPagination(pagination, result.count),
      };
    } catch {
      return { data: [], pagination: getResultsPagination(pagination) };
    }
  }

  public async count(
    column?: string,
    value?: string | null | string[],
    args?: { filters?: QueryFilter[]; count?: 'exact' | 'planned' | 'estimated' }
  ): Promise<number> {
    if (column && value) {
      const searchValue = Array.isArray(value) ? value : [value];
      const result = await (() => {
        const r = this.table
          .select(this.pk, { count: args?.count ?? 'estimated', head: true })
          .in(column, searchValue);

        if (args?.filters) {
          args.filters.forEach((filter) => {
            r.filter(filter[0], filter[1], cleanFilterValue(filter[1], filter[2]));
          });
        }

        return r;
      })();
      return result.count || 0;
    }
    const result = await this.table.select(this.pk, { count: 'estimated', head: true });
    return result.count || 0;
  }

  public update(pkValue: string, data: UpdateSchema) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.table.update(data as any).eq(this.pk, pkValue);
  }

  public insert(data: InsertSchema | InsertSchema[], select: string | undefined = undefined) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (Array.isArray(data)) {
      return this.table.insert(data as any).select(select);
    } else {
      return this.table
        .insert(data as any)
        .select(select)
        .single();
    }
  }

  public upsert(data: InsertSchema | InsertSchema[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.table
      .upsert(data as any)
      .select()
      .single();
  }

  public destroy(profileId: string, id: string) {
    return this.table.delete().eq('id', id).eq('profile_id', profileId);
  }
}
